<!-- Utilization_FastFacts.vue — graph-sourced single-patient utilization + cost, last 12 mo -->
<!--
  Computes entirely from the patient graph payload (claims + adt_episodes).
  No new backend call. IP/ED/SNF from ADT episodes; cost from claim paid amounts.
-->
<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Utilization · Last {{ lookbackMonths }} mo
      </div>
      <div class="flex items-center gap-3">
        <router-link
          v-if="patientIdentifier"
          :to="`/patients/${patientIdentifier}`"
          class="text-[11px] text-blue-500 hover:text-blue-700 transition-colors inline-flex items-center gap-1"
          title="Open full patient record"
        >
          Details
          <FontAwesomeIcon :icon="['fas', 'arrow-up-right-from-square']" class="text-[9px]" />
        </router-link>
        <button
          :disabled="loading"
          class="text-gray-300 hover:text-gray-500 transition-colors"
          title="Refresh"
          @click="$emit('refresh')"
        >
          <FontAwesomeIcon :icon="['fas', 'sync']" :class="{ 'animate-spin': loading }" class="text-[11px]" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-4">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-300 animate-spin text-sm" />
    </div>

    <!-- Not in graph -->
    <p v-else-if="notInGraph" class="text-xs text-gray-400">Claims data not available for this patient.</p>

    <!-- Empty -->
    <p v-else-if="!hasAnyData" class="text-xs text-gray-400">No utilization or cost in the last {{ lookbackMonths }} months.</p>

    <!-- Facts -->
    <div v-else class="grid grid-cols-4 gap-px bg-gray-100 rounded-lg overflow-hidden border border-gray-100">
      <div
        v-for="f in facts"
        :key="f.label"
        class="bg-white px-2 py-2.5 text-center"
      >
        <FontAwesomeIcon :icon="['fas', f.icon]" :class="f.iconClass" class="text-[13px] mb-1" />
        <div class="text-lg font-semibold tabular-nums leading-none" :class="f.valueClass">{{ f.value }}</div>
        <div class="text-[10px] text-gray-400 mt-1">{{ f.label }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const SNF_DISPOSITIONS = ['SNF', 'SKILLED NURSING', 'NURSING FACILITY'];

export default {
  name: 'Utilization_FastFacts',
  components: { FontAwesomeIcon },
  props: {
    claims: { type: Array, default: () => [] },
    adtEpisodes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    lookbackMonths: { type: Number, default: 12 },
    notInGraph: { type: Boolean, default: false },
    patientIdentifier: { type: [String, Number], default: null },
  },
  emits: ['refresh'],
  computed: {
    cutoffDate() {
      const d = new Date();
      d.setMonth(d.getMonth() - this.lookbackMonths);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    recentEpisodes() {
      return (this.adtEpisodes || []).filter((e) => {
        const dt = this.parseDate(e.EPISODE_START || e.FIRST_IP_ADMIT || e.FIRST_ED_ARRIVAL);
        return dt && dt >= this.cutoffDate;
      });
    },
    recentClaims() {
      return (this.claims || []).filter((c) => {
        const dt = this.parseDate(c.CLM_THRU_DT || c.CLM_FROM_DT);
        return dt && dt >= this.cutoffDate;
      });
    },
    util() {
      let ipAdmits = 0;
      let snfStays = 0;
      const edDates = new Set();
      for (const e of this.recentEpisodes) {
        const type = (e.EPISODE_TYPE || '').toUpperCase();
        if (type.includes('IP') || type.includes('INPATIENT')) ipAdmits += 1;
        else if (type.includes('ED') || type.includes('EMERGENCY')) {
          const dt = this.parseDate(e.EPISODE_START || e.FIRST_ED_ARRIVAL);
          if (dt) edDates.add(this.dateKey(dt));
        }
        const disp = (e.DISCHARGE_DISPOSITION || '').toUpperCase();
        if (SNF_DISPOSITIONS.some((s) => disp.includes(s))) snfStays += 1;
      }
      for (const c of this.recentClaims) {
        if (!c.IS_ED_VISIT) continue;
        const dt = this.parseDate(c.CLM_FROM_DT || c.CLM_THRU_DT);
        if (dt) edDates.add(this.dateKey(dt));
      }
      if (snfStays === 0) snfStays = this.countSnfClaims();
      return { ipAdmits, edVisits: edDates.size, snfStays };
    },
    cost() {
      let total = 0;
      for (const c of this.recentClaims) total += this.toNum(c.CLM_PMT_AMT);
      return { total };
    },
    hasAnyData() {
      return this.recentEpisodes.length > 0 || this.cost.total > 0;
    },
    facts() {
      return [
        { label: 'IP Admits', value: this.util.ipAdmits, icon: 'hospital', iconClass: 'text-indigo-400', valueClass: 'text-indigo-600' },
        { label: 'ED Visits', value: this.util.edVisits, icon: 'truck-medical', iconClass: 'text-rose-400', valueClass: 'text-rose-600' },
        { label: 'SNF Stays', value: this.util.snfStays, icon: 'bed-pulse', iconClass: 'text-amber-400', valueClass: 'text-amber-600' },
        { label: 'Total Cost', value: this.fmtMoney(this.cost.total), icon: 'sack-dollar', iconClass: 'text-emerald-400', valueClass: 'text-emerald-600' },
      ];
    },
  },
  methods: {
    parseDate(raw) {
      if (!raw) return null;
      const m = String(raw).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return null;
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    },
    dateKey(date) {
      const pad = (value) => String(value).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    toNum(v) {
      if (v === null || v === undefined || v === '') return 0;
      const n = Number(String(v).replace(/[^0-9.\-]/g, ''));
      return Number.isFinite(n) ? n : 0;
    },
    countSnfClaims() {
      const months = new Set();
      for (const c of this.recentClaims) {
        if ((c.CLM_TYPE_LABEL || '').toUpperCase() === 'SNF') {
          const dt = this.parseDate(c.CLM_FROM_DT || c.CLM_THRU_DT);
          if (dt) months.add(`${dt.getFullYear()}-${dt.getMonth()}`);
        }
      }
      return months.size;
    },
    fmtMoney(v) {
      const n = this.toNum(v);
      if (n >= 1000) return '$' + Math.round(n / 1000) + 'k';
      return '$' + Math.round(n);
    },
  },
};
</script>
